import {
  OWNER_INQUIRY_MAX_IMAGES,
  createInquiryId,
  type OwnerInquiryImage,
} from "@/domain/owner-inquiry";

const MAX_EDGE = 1280;
const JPEG_QUALITY = 0.72;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("画像を読めませんでした。"));
    img.src = src;
  });
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") resolve(reader.result);
      else reject(new Error("画像の読み込みに失敗しました。"));
    };
    reader.onerror = () => reject(new Error("画像の読み込みに失敗しました。"));
    reader.readAsDataURL(file);
  });
}

/** Compress a picked/captured image for inquiry storage + share. */
export async function compressInquiryImage(file: File): Promise<OwnerInquiryImage> {
  if (!file.type.startsWith("image/")) {
    throw new Error("画像ファイルを選んでください。");
  }
  const raw = await readFileAsDataUrl(file);
  const img = await loadImage(raw);
  const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(1, Math.round(img.naturalWidth * scale));
  const h = Math.max(1, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("画像の圧縮に失敗しました。");
  ctx.drawImage(img, 0, 0, w, h);
  const dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
  const base = file.name.replace(/\.[^.]+$/, "") || "photo";
  return {
    id: createInquiryId(),
    name: `${base.slice(0, 40)}.jpg`,
    dataUrl,
  };
}

export async function appendInquiryImages(
  current: OwnerInquiryImage[],
  files: FileList | File[],
): Promise<{ images: OwnerInquiryImage[]; error: string | null }> {
  const list = Array.from(files);
  if (list.length === 0) return { images: current, error: null };
  const room = OWNER_INQUIRY_MAX_IMAGES - current.length;
  if (room <= 0) {
    return {
      images: current,
      error: `画像は最大${OWNER_INQUIRY_MAX_IMAGES}枚までです。`,
    };
  }
  const take = list.slice(0, room);
  const next = [...current];
  for (const file of take) {
    next.push(await compressInquiryImage(file));
  }
  const truncated = list.length > room;
  return {
    images: next,
    error: truncated
      ? `画像は最大${OWNER_INQUIRY_MAX_IMAGES}枚までです（超過分は捨てました）。`
      : null,
  };
}

/** Collect image files from a paste / drop clipboard payload. */
export function imageFilesFromDataTransfer(
  data: DataTransfer | null,
): File[] {
  if (!data) return [];
  const out: File[] = [];
  if (data.files?.length) {
    for (const f of Array.from(data.files)) {
      if (f.type.startsWith("image/")) out.push(f);
    }
  }
  if (out.length > 0) return out;
  for (const item of Array.from(data.items ?? [])) {
    if (item.kind === "file" && item.type.startsWith("image/")) {
      const f = item.getAsFile();
      if (f) out.push(f);
    }
  }
  return out;
}


export async function dataUrlToFile(
  dataUrl: string,
  name: string,
): Promise<File> {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], name, { type: blob.type || "image/jpeg" });
}
